import {
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  computed,
  input,
  effect,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { TransactionLogsPanelComponent, TransactionLogsPanelConfig, TransactionLogColumn, TransactionLogFilter, MonthYearOption } from '../../../../../shared/components/transaction-logs-panel/transaction-logs-panel.component';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { LineAreaChartComponent, ILineAreaSeries, ILineAreaDataPoint } from '../../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { ToggleBtnsComponent, toggleHeader } from '../../../../../shared/components/toggle-btns/toggle-btns.component';
import { ScheduledTodayRecord, MostRecentAttemptsRecord, ScheduledTodayAPI, MostRecentAttemptsAPI, PendingByGapAPI, PendingByGapItem, PaymentOutcomesBySlotAPI, RecoveryStatusAPI, AvgRecoveryAmountAPI, DuplicatesSummaryStatsAPI, DuplicatesByAdminAPI } from '../../interface/arrangment-cron-api.interface';
import { ArrangmentCronApiService } from '../../service/api-service.service';
import { PendingAnalyticsService, RecordsByPeriod } from '../../services/pending-analytics.service';
import { VerticalSingleBarComponent } from '../../../../../shared/components/charts/bar-charts/vertical-single-bar/vertical-single-bar.component';
import { ScrollTableComponent, TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { ThemeService } from '../../../../../core/services/theme.service';
import {
  ArrangementCronOverviewSectionDef,
  ArrangementCronOverviewSectionId,
  ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS,
  DashboardPageKey,
  isArrangementCronOverviewSectionId,
} from '../../../../../core/enums/dashboard-cards.enum';
import { ARRANGMENT_CRON_DEFAULT_COL_SPAN } from '../../arrangment-cron-layout.catalog';
import {
  arrangmentSectionColClass,
  arrangmentSectionOrder,
} from '../../arrangment-cron-section-layout';
import {
  createEmbeddedSectionFilterByWidgetKeyMap,
  findCatalogPageId,
  resolveActiveCatalogProductId,
  resolveWidgetKeyToSectionId,
  stripProductPrefix,
} from '../../../../../shared/components/dashboard/dashboard-embed.helpers';
import { CustomDashboardPinService } from '../../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../../core/services/customize-layout-batch.service';
import { ToasterService } from '../../../../../core/services/toaster.service';
import { ApiService } from '../../../../../core/services/api.service';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  normalizeLayoutEditorColSpan,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { DashboardLayoutEditorComponent } from '../../../../../shared/components/dashboard/dashboard-layout-editor.component';

import { IndividualToasterComponent } from '../../../../../shared/components/loader/loader.component';

/** Persisted layout state per overview section. */
interface OverviewSectionLayoutItem {
  id: ArrangementCronOverviewSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

/** Draft row used by the Customize Layout editor. */
interface OverviewLayoutEditorItem extends OverviewSectionLayoutItem {
  title: string;
  defaultColSpan?: number | null;
}

@Component({
  selector: 'app-overview-new',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    IconComponentV2,
    TransactionLogsPanelComponent,
    LineAreaChartComponent,
    ToggleBtnsComponent,
    VerticalSingleBarComponent,
    ScrollTableComponent,
    // IndividualToasterComponent,
    DashboardLayoutEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './overview-new.component.html',
})
export class OverviewNewComponent implements OnInit, OnDestroy {
  private arrangmentCronApiService = inject(ArrangmentCronApiService);
  private analyticsService = inject(PendingAnalyticsService);
  private themeService = inject(ThemeService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private toaster = inject(ToasterService);
  private apiService = inject(ApiService);

  /** Mirrors daily/overdue-cron — enables My Dashboard embed filtering. */
  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.ARRANGEMENT_CRON_OVERVIEW;

  readonly SectionId = ArrangementCronOverviewSectionId;
  get SECTIONS() {
    const hasData = !this.isLoadingDuplicatesByAdmin() && this.duplicatesByAdmin()?.records && this.duplicatesByAdmin()!.records.length > 0;
    return ArrangementCronOverviewSectionDef.filter(s =>
      s.id !== ArrangementCronOverviewSectionId.ADMIN_DUPLICATES_TABLE || hasData
    );
  }

  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<
    string,
    ArrangementCronOverviewSectionId
  > = new Map(
    (
      Object.entries(ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS) as [
        ArrangementCronOverviewSectionId,
        string,
      ][]
    ).map(([sectionId, backendKey]) => [backendKey, sectionId]),
  );

  // ─── Customize Layout (visibility + colSpan + order) ───────────────────
  readonly layoutMap = signal<
    Record<ArrangementCronOverviewSectionId, OverviewSectionLayoutItem>
  >({} as Record<ArrangementCronOverviewSectionId, OverviewSectionLayoutItem>);

  readonly orderedSections = computed(() => {
    const sections = this.SECTIONS;
    const layout = this.layoutMap();
    return sections
      .map((s) => layout[s.id as ArrangementCronOverviewSectionId])
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
  });

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const canonical = stripProductPrefix(k);
      if (canonical !== k) allowed.add(canonical);
    }
    return allowed;
  });

  showSection(id: ArrangementCronOverviewSectionId): boolean {
    if (this.mixedMode()) {
      const active = this.activeSectionIds();
      return active.has(id);
    }
    return this.layoutMap()[id]?.visible ?? true;
  }

  trackSection = (_idx: number, sec: OverviewSectionLayoutItem): string => sec.id;

  sectionOrder(sectionId: string): number {
    return arrangmentSectionOrder(this._layoutRecord(), sectionId);
  }

  getSectionColClass(sectionId: string): string {
    if (this.mixedMode()) return 'col-span-12';
    return arrangmentSectionColClass(this._layoutRecord(), sectionId);
  }

  private _layoutRecord(): Record<string, OverviewSectionLayoutItem> {
    return this.layoutMap() as unknown as Record<string, OverviewSectionLayoutItem>;
  }

  // ─── Pin to Custom Dashboard ───────────────────────────────────────────
  readonly pinnedSections = signal<Set<ArrangementCronOverviewSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<ArrangementCronOverviewSectionId>>(new Set());
  readonly pinnableWidgetIds = signal<Map<ArrangementCronOverviewSectionId, number>>(
    new Map(),
  );

  // ─── Customize Layout editor ─────────────────────────────────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<OverviewLayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_arrangment_cron_ov_layout_v1';
  private _layoutSnapshot: Record<
    ArrangementCronOverviewSectionId,
    OverviewSectionLayoutItem
  > | null = null;

  trackEditorItem = (_idx: number, item: OverviewLayoutEditorItem): string => item.id;

  constructor() {
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(
            catalog as any[],
            this.PAGE_KEY,
            activeProductPid,
          );
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<ArrangementCronOverviewSectionId>();
          keys.forEach((k) => {
            const sectionId = resolveWidgetKeyToSectionId(k, this.WIDGET_KEY_TO_SECTION);
            if (sectionId != null && isArrangementCronOverviewSectionId(sectionId)) {
              pinned.add(sectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<ArrangementCronOverviewSectionId, number>();
            for (const sec of Object.values(ArrangementCronOverviewSectionId)) {
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  isExpanded1 = signal<boolean>(false);
  openPointerIndex = signal<number | null>(null);

  isLoadingScheduledToday = signal<boolean>(false);
  isLoadingRecentAttempts = signal<boolean>(false);
  isLoadingPendingByGap = signal<boolean>(false);
  isLoadingPaymentOutcome = signal<boolean>(false);
  isLoadingRecoveryStatus = signal<boolean>(false);
  isLoadingRecoveryAverage = signal<boolean>(false);
  isLoadingDuplicatesSummary = signal<boolean>(false);
  isLoadingDuplicatesByAdmin = signal<boolean>(false);

  // Transaction logs panel state
  isPanelOpen = signal<boolean>(false);
  panelConfig = signal<TransactionLogsPanelConfig | null>(null);

  scheduledToday = signal<{
    cycle_date: string;
    total: number;
    total_slots: { key: string; label: string; value: number }[];
    records: { key: string; label: string; value: ScheduledTodayRecord[] }[];
  } | null>(null);

  recentAttempts = signal<{
    cycle_date: string;
    total: number;
    total_success: number;
    total_failed: number;
    total_slots: { key: string; label: string; total: number, success: number; failed: number; card: number; ach: number; }[];
    records: MostRecentAttemptsRecord[];
  } | null>(null);

  pendingByGap = signal<PendingByGapAPI | null>(null);
  paymentOutcome = signal<PaymentOutcomesBySlotAPI | null>(null);
  recoveryStatus = signal<RecoveryStatusAPI | null>(null);
  recoveryAverage = signal<AvgRecoveryAmountAPI | null>(null);
  duplicatesSummary = signal<DuplicatesSummaryStatsAPI | null>(null);
  duplicatesByAdmin = signal<DuplicatesByAdminAPI | null>(null);

  pendingByGapUniqueAdmins = signal<number>(0);
  selectedPaymentTab = signal<'recurring' | 'partial'>('recurring');

  // Duplicates table configuration
  duplicatesTableHeaders: TableHeader[] = [
    { label: 'S No', key: 'index', align: 'text-left', noTransform: true },
    { label: 'Admin Name', key: 'admin_name', align: 'text-left', noTransform: true },
    { label: 'Duplicates', key: 'duplicate_entries', align: 'text-center', noTransform: true, textClass: 'font-bold text-orange-700 dark:text-orange-300' },
    { label: 'Deletions', key: 'deletion_entries', align: 'text-center', noTransform: true, textClass: 'font-bold text-red-700 dark:text-red-300' },
    { label: 'Contracts w/Duplicates', key: 'contracts_with_duplicates', align: 'text-center', noTransform: true },
    { label: 'Affected', key: 'contracts_affected', align: 'text-center', noTransform: true },
  ];


  gapInsights = signal<{
    topAdmin: { name: string; count: number; totalAmount: number } | null;
    peakMonth: { label: string; count: number; totalAmount: number } | null;
    paymentType: { name: string; count: number; percentage: number; totalAmount: number } | null;
    avgDays: { value: number; earliest: string; latest: string } | null;
    recurring: { count: number; totalAmount: number; percentage: number } | null;
    partial: { count: number; totalAmount: number; percentage: number } | null;
    totalPending: number;
  }>({
    topAdmin: null,
    peakMonth: null,
    paymentType: null,
    avgDays: null,
    recurring: null,
    partial: null,
    totalPending: 0,
  });

  headerCards = signal([
    {
      label: 'Total Arrangements',
      value: 0,
      subValue: 0,
    },
    {
      label: 'Success Rate',
      value: 0,
      subValue: 0,
    },
    {
      label: 'Total Recovered',
      value: 0,
      subValue: 0,
    },
    {
      label: 'Avg Recovery Amount',
      value: 0,
      subValue: 0,
    }
  ]);

  toggleExpand1() {
    this.isExpanded1.set(!this.isExpanded1());
  }

  togglePointer(index: number): void {
    this.openPointerIndex.set(this.openPointerIndex() === index ? null : index);
  }

  onPaymentTabChange(value: string): void {
    if (value === 'recurring' || value === 'partial') {
      this.selectedPaymentTab.set(value);
    }
  }


  ngOnInit(): void {
    this._initLayout();

    if (this.showSection(ArrangementCronOverviewSectionId.SCHEDULED_TODAY)) {
      this.getScheduledToday();
    }
    if (this.showSection(ArrangementCronOverviewSectionId.RECENT_ATTEMPTS)) {
      this.getRecentAttempt();
    }
    if (
      this.showSection(ArrangementCronOverviewSectionId.KPI_SUMMARY) ||
      this.showSection(ArrangementCronOverviewSectionId.SYSTEM_INTEL) ||
      this.showSection(ArrangementCronOverviewSectionId.TIME_GAP_DISTRIBUTION) ||
      this.showSection(ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW) ||
      this.showSection(ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION) ||
      this.showSection(ArrangementCronOverviewSectionId.CREATION_LIFECYCLE) ||
      this.showSection(ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART)
    ) {
      this.getHeaderCardsValue();
    }
    if (this.showSection(ArrangementCronOverviewSectionId.METRIC_ANOMALIES)) {
      this.getDuplicatesSummary();
    }
    if (this.showSection(ArrangementCronOverviewSectionId.ADMIN_DUPLICATES_TABLE)) {
      this.getDuplicatesByAdmin();
    }
  }

  onDateRangeChange(event: DateRangePickerOutput): void {
    // Overview tab is designed for all-time data view, so date changes are ignored
  }

  ngOnDestroy(): void {
    this.isLoadingScheduledToday.set(false);
    this.isLoadingRecentAttempts.set(false);
  }

  private getScheduledToday(): void {
    this.isLoadingScheduledToday.set(true);
    this.arrangmentCronApiService.getScheduledToday({ is_dummy: 0 }, { showLoader: false, cache: false }).subscribe({
      next: (response: ScheduledTodayAPI) => {
        this.isLoadingScheduledToday.set(false);

        const data = {
          cycle_date: response.date,
          total: response.total_arrangements,
          total_slots: [
            { key: 'morning', label: 'Morning', value: response.by_slot.morning.total },
            { key: 'afternoon', label: 'Afternoon', value: response.by_slot.afternoon.total },
            { key: 'evening', label: 'Evening', value: response.by_slot.evening.total },
          ],
          records: [
            { key: 'morning', label: 'Morning', value: response.by_slot.morning.records },
            { key: 'afternoon', label: 'Afternoon', value: response.by_slot.afternoon.records },
            { key: 'evening', label: 'Evening', value: response.by_slot.evening.records },
          ],
        };

        this.scheduledToday.set(data);
      },
      error: (error: any) => {
        this.isLoadingScheduledToday.set(false);
        console.error('Error retrieving scheduled today:', error);
      }
    })
  }

  private getRecentAttempt(): void {
    this.isLoadingRecentAttempts.set(true);
    this.arrangmentCronApiService.getMostRecentAttempts({ is_dummy: 0 }, { showLoader: false, cache: false }).subscribe({
      next: (response: MostRecentAttemptsAPI) => {
        this.isLoadingRecentAttempts.set(false);
        const data = {
          cycle_date: response.cycle_date,
          total: response.total_arrangements,
          total_success: response.by_slot.morning.successful + response.by_slot.afternoon.successful + response.by_slot.evening.successful,
          total_failed: response.by_slot.morning.failed + response.by_slot.afternoon.failed + response.by_slot.evening.failed,
          total_slots: [
            { key: 'morning', label: 'Morning', total: response.by_slot.morning.total, success: response.by_slot.morning.successful, failed: response.by_slot.morning.failed, card: response.by_slot.morning.card, ach: response.by_slot.morning.ach },
            { key: 'afternoon', label: 'Afternoon', total: response.by_slot.afternoon.total, success: response.by_slot.afternoon.successful, failed: response.by_slot.afternoon.failed, card: response.by_slot.afternoon.card, ach: response.by_slot.afternoon.ach },
            { key: 'evening', label: 'Evening', total: response.by_slot.evening.total, success: response.by_slot.evening.successful, failed: response.by_slot.evening.failed, card: response.by_slot.evening.card, ach: response.by_slot.evening.ach },
          ],
          records: response.records,
        };

        this.recentAttempts.set(data);
      },
      error: (error: any) => {
        this.isLoadingRecentAttempts.set(false);
        console.error('Error retrieving recent attempts:', error);
      }
    })
  }

  private getHeaderCardsValue(): void {
    this.isLoadingPendingByGap.set(true);
    this.isLoadingPaymentOutcome.set(true);
    this.isLoadingRecoveryStatus.set(true);
    this.isLoadingRecoveryAverage.set(true);

    forkJoin({
      pendingByGap: this.arrangmentCronApiService.getPendingByGap({ is_dummy: 0, include_records: true }, { cache: false, showLoader: false }),
      paymentOutcome: this.arrangmentCronApiService.getPaymentOutcomesBySlot({ is_dummy: 0, start_time: '2017-01-01' }, { showLoader: false }),
      recoveryStatus: this.arrangmentCronApiService.getRecoveryStatus({ is_dummy: 0, start_time: '2017-01-01' }, { showLoader: false }),
      recoveryAverage: this.arrangmentCronApiService.getAvgRecoveryAmount({ is_dummy: 0, start_time: '2017-01-01' }, { showLoader: false }),
    }).subscribe({
      next: ({ pendingByGap, paymentOutcome, recoveryStatus, recoveryAverage }) => {
        this.isLoadingPendingByGap.set(false);
        this.isLoadingPaymentOutcome.set(false);
        this.isLoadingRecoveryStatus.set(false);
        this.isLoadingRecoveryAverage.set(false);

        this.pendingByGap.set(pendingByGap);
        this.paymentOutcome.set(paymentOutcome);
        this.recoveryStatus.set(recoveryStatus);
        this.recoveryAverage.set(recoveryAverage);

        // Set analytics service data for chart computations
        this.analyticsService.setData(pendingByGap);

        // Update header cards with fetched values
        const totalPending = pendingByGap?.summary?.total_pending ?? 0;
        const totalExecuted = paymentOutcome?.stats?.total_executed ?? 0;
        const totalArrangements = recoveryStatus?.stats?.total_arrangements ?? 0;
        const totalSuccessful = paymentOutcome?.stats?.total_successful ?? 0;
        const totalRecoveredAmount = recoveryStatus?.stats?.total_recovered_amount ?? 0;
        const overallRecoveryRate = recoveryStatus?.stats?.overall_recovery_rate ?? 0;
        const avgRecoveryAmount = recoveryAverage?.avg_recovery_amount ?? 0;

        const records = pendingByGap?.by_gap.flatMap(gap => gap.records ?? []);
        const admins = records.map(record => record['created_by'] ?? null).filter(Boolean);
        const uniqueAdmins = admins.filter((record: any, index: number, self: any) => {
          return index === self.findIndex((t: any) => t === record);
        });

        this.pendingByGapUniqueAdmins.set(uniqueAdmins.length);

        // Compute gap insights
        this.computeGapInsights(pendingByGap, records);

        this.headerCards.set([
          {
            label: 'Total Arrangements',
            value: totalArrangements,
            subValue: totalPending,
          },
          {
            label: 'Success Rate',
            value: totalExecuted > 0 ? Math.round((totalSuccessful / totalExecuted) * 100) : 0,
            subValue: totalSuccessful,
          },
          {
            label: 'Total Recovered',
            value: totalRecoveredAmount,
            subValue: overallRecoveryRate,
          },
          {
            label: 'Avg Recovery Amount',
            value: avgRecoveryAmount,
            subValue: recoveryAverage?.total_successful ?? 0,
          }
        ]);
      },
      error: (error: any) => {
        this.isLoadingPendingByGap.set(false);
        this.isLoadingPaymentOutcome.set(false);
        this.isLoadingRecoveryStatus.set(false);
        this.isLoadingRecoveryAverage.set(false);
        console.error('Error retrieving header cards data:', error);
      }
    });
  }

  private getDuplicatesSummary(): void {
    this.isLoadingDuplicatesSummary.set(true);
    const startTime = "2017-01-01";
    const endTime = dayjs.utc().tz(LA_TIMEZONE).format('YYYY-MM-DD');

    this.arrangmentCronApiService.getDuplicatesSummaryStats({
      start_time: startTime,
      end_time: endTime,
      is_dummy: 0
    }, { cache: false, showLoader: false }).subscribe({
      next: (response: DuplicatesSummaryStatsAPI) => {
        this.isLoadingDuplicatesSummary.set(false);
        this.duplicatesSummary.set(response);
      },
      error: (error: any) => {
        this.isLoadingDuplicatesSummary.set(false);
        console.error('Error retrieving duplicates summary:', error);
      }
    });
  }

  private getDuplicatesByAdmin(): void {
    this.isLoadingDuplicatesByAdmin.set(true);
    const startTime = "2017-01-01";
    const endTime = dayjs.utc().tz(LA_TIMEZONE).format('YYYY-MM-DD');

    this.arrangmentCronApiService.getDuplicatesByAdmin({
      start_time: startTime,
      end_time: endTime,
      is_dummy: 0,
      include_records: true
    }, { cache: false, showLoader: false }).subscribe({
      next: (response: DuplicatesByAdminAPI) => {
        this.isLoadingDuplicatesByAdmin.set(false);
        this.duplicatesByAdmin.set(response);
      },
      error: (error: any) => {
        this.isLoadingDuplicatesByAdmin.set(false);
        console.error('Error retrieving duplicates by admin:', error);
      }
    });
  }

  openScheduledTodayPanel(): void {
    const data = this.scheduledToday();
    if (!data) return;

    // Flatten records from all slots and mark low amount records
    const allRecords: any[] = [];
    data.records.forEach(slotData => {
      slotData.value.forEach((record: any) => {
        // Check if it's a partial payment with amount <= 1
        const isLowAmount = record.arrangement_payment_type === 2 &&
          (record.partial_amount <= 1 || record.arrangement_amount <= 1);

        const isNormalAmount = record.arrangement_payment_type === 2 &&
          (record.partial_amount > 1 && record.arrangement_amount > 1) || record.arrangement_payment_type === 1;

        // Map pf_status to string
        let statusStr = 'Unknown';
        const pfStatus = record.pf_status;
        if (pfStatus === 1 || pfStatus === 2) statusStr = 'Active';
        else if (pfStatus === 3) statusStr = 'Overdue';
        else if (pfStatus === 4) statusStr = 'Completed';
        else if (pfStatus === 5) statusStr = 'Cancelled';

        // Fallback for demo/dummy data if needed, or if status comes from elsewhere
        if (record.status) statusStr = record.status;

        allRecords.push({
          ...record,
          slot_key: slotData.key,
          slot_label: slotData.label,
          is_low_amount: isLowAmount,
          is_normal_amount: isNormalAmount,
          status: statusStr // Helper field for display
        });
      });
    });

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract / Admin', type: 'text', width: '20%' },
      { key: 'arrangement_date', label: 'Date / Slot', type: 'date', width: '18%' },
      { key: 'payment_type_name', label: 'Type / Method', type: 'badge', width: '15%' },
      { key: 'arrangement_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'status', label: 'Status', type: 'status', width: '12%' },
      { key: 'arrangement_note', label: 'Note', type: 'text', width: '23%' },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'morning', label: 'Morning' },
      { key: 'afternoon', label: 'Afternoon' },
      { key: 'evening', label: 'Evening' },
      { key: 'low_amount', label: '≤ $1 Partial' },
      { key: 'normal_amount', label: '> $1 Partial/Recurring' },
    ];

    this.panelConfig.set({
      title: 'Scheduled Today',
      subtitle: `Detailed breakdown of arrangement activities`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'indigo',
    });

    this.isPanelOpen.set(true);
  }

  openRecentAttemptsPanel(): void {
    const data = this.recentAttempts();
    if (!data) return;

    // Add slot info to records if available
    const allRecords = data.records.map((record: any) => ({
      ...record,
      slot_key: (record.slot || record.slot_name)?.toLowerCase() || '',
      slot_label: record.slot || record.slot_name || '',
    }));

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract / Admin', type: 'text', width: '20%' },
      { key: 'execution_time', label: 'Date / Slot', type: 'date', width: '18%' },
      { key: 'payment_type_name', label: 'Type / Method', type: 'badge', width: '15%' },
      { key: 'arrangement_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'status', label: 'Status', type: 'status', width: '12%' },
      { key: 'arrangement_note', label: 'Note', type: 'text', width: '23%' },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'morning', label: 'Morning' },
      { key: 'afternoon', label: 'Afternoon' },
      { key: 'evening', label: 'Evening' },
    ];

    this.panelConfig.set({
      title: 'Recent Attempts',
      subtitle: `Detailed breakdown of arrangement activities`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'emerald',
    });

    this.isPanelOpen.set(true);
  }

  openPendingByGapPanel(gap: PendingByGapItem, index: number): void {
    if (!gap?.records || gap.records.length === 0) return;

    const isLast = index === (this.pendingByGap()?.by_gap?.length ?? 0) - 1;
    const accentColor = isLast ? 'rose' : index === 0 ? 'emerald' : index === 1 ? 'indigo' : index === 2 ? 'amber' : 'indigo';

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract / Patient', type: 'text', width: '18%' },
      { key: 'arrangement_date', label: 'Date / Slot', type: 'date', width: '15%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '12%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'days_until', label: 'Days Until', type: 'text', width: '10%' },
      { key: 'contract_status', label: 'Status', type: 'status', width: '12%' },
      { key: 'arrangement_note', label: 'Note', type: 'text', width: '21%' },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    // Map records to include slot info for filtering
    const allRecords = gap.records.map((record: any) => ({
      ...record,
      slot_key: record.slot_name?.toLowerCase() || '',
      slot_label: record.slot_name || '',
      payment_type_key: record.payment_type_name?.toLowerCase() || '',
      created_by: record.created_by || record.patient_name,
    }));

    this.panelConfig.set({
      title: `Pending: ${gap.gap_range} Days`,
      subtitle: `${gap.total_arrangements} arrangements scheduled between ${gap.earliest_date} and ${gap.latest_date}`,
      data: allRecords,
      columns,
      filters,
      accentColor,
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  closePanel(): void {
    this.isPanelOpen.set(false);
    this.panelConfig.set(null);
  }

  openRecurringAnalysisPanel(): void {
    // Combine all 4 charts data with category tags
    const allRecords: any[] = [];

    // Add Highest Recurring records
    this.highestRecurringAmounts().forEach((record, index) => {
      allRecords.push({
        ...record,
        category: 'Highest Recurring',
        categoryType: 'recurring',
        rank: index + 1,
        amount: record.recurringAmount,
        amountType: 'recurring_amount'
      });
    });

    // Add Lowest Recurring records
    this.lowestRecurringAmounts().forEach((record, index) => {
      allRecords.push({
        ...record,
        category: 'Lowest Recurring',
        categoryType: 'recurring',
        rank: index + 1,
        amount: record.recurringAmount,
        amountType: 'recurring_amount'
      });
    });

    // Add Highest Partial records
    this.highestPartialAmounts().forEach((record, index) => {
      allRecords.push({
        ...record,
        category: 'Highest Partial',
        categoryType: 'partial',
        rank: index + 1,
        amount: record.partialAmount,
        amountType: 'partial_amount'
      });
    });

    // Add Lowest Partial records
    this.lowestPartialAmounts().forEach((record, index) => {
      allRecords.push({
        ...record,
        category: 'Lowest Partial',
        categoryType: 'partial',
        rank: index + 1,
        amount: record.partialAmount,
        amountType: 'partial_amount'
      });
    });

    const columns: TransactionLogColumn[] = [
      { key: 'rank', label: '#', type: 'text', width: '50px' },
      { key: 'category', label: 'Category', type: 'badge', width: '140px' },
      { key: 'patientName', label: 'Patient Name', type: 'text', searchable: true },
      { key: 'contractCode', label: 'Contract', type: 'text', width: '120px', searchable: true },
      { key: 'amount', label: 'Amount', type: 'currency', width: '120px' },
      { key: 'remainingPayments', label: 'Remaining', type: 'text', width: '90px' },
      { key: 'createdBy', label: 'Created By', type: 'text', width: '140px', searchable: true },
      { key: 'gapRange', label: 'Gap', type: 'text', width: '80px' },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'Highest Recurring', label: 'Highest Recurring' },
      { key: 'Lowest Recurring', label: 'Lowest Recurring' },
      { key: 'Highest Partial', label: 'Highest Partial' },
      { key: 'Lowest Partial', label: 'Lowest Partial' },
    ];

    this.panelConfig.set({
      title: 'Payment Amount Distribution',
      subtitle: `Pending arrangements ranked by scheduled payment amounts — Recurring (fixed monthly) & Partial (one-time/variable)`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'indigo',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for all pending arrangements
  openAllPendingPanel(): void {
    const pendingData = this.pendingByGap();
    if (!pendingData) return;

    const allRecords = pendingData.by_gap.flatMap(gap =>
      (gap.records || []).map((record: any) => ({
        ...record,
        gapRange: gap.gap_range,
        slot_key: record.slot_name?.toLowerCase() || '',
        payment_type_key: record.payment_type_name?.toLowerCase() || '',
      }))
    );

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'slot_name', label: 'Slot', type: 'text', width: '10%' },
      { key: 'gapRange', label: 'Gap Range', type: 'text', width: '10%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    this.panelConfig.set({
      title: 'All Pending Arrangements',
      subtitle: `${allRecords.length} arrangements scheduled across all time gaps`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'indigo',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for far future arrangements (>90 days)
  openFarFuturePanel(): void {
    const pendingData = this.pendingByGap();
    if (!pendingData) return;

    // Get records from the last gap (90+ days)
    const farFutureGap = pendingData.by_gap[pendingData.by_gap.length - 1];
    if (!farFutureGap?.records) return;

    const allRecords = farFutureGap.records.map((record: any) => ({
      ...record,
      gapRange: farFutureGap.gap_range,
      slot_key: record.slot_name?.toLowerCase() || '',
      payment_type_key: record.payment_type_name?.toLowerCase() || '',
    }));

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'days_until', label: 'Days Until', type: 'text', width: '10%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    this.panelConfig.set({
      title: 'Far Future Arrangements (90+ Days)',
      subtitle: `${allRecords.length} arrangements scheduled far in the future — may need review`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'rose',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for arrangements by status
  openStatusPanel(status?: string): void {
    const statusData = this.recordsByStatus();
    if (!statusData || statusData.length === 0) return;

    let allRecords: any[] = [];

    if (status) {
      // Filter by specific status
      const statusGroup = statusData.find(s => s.status === status);
      if (statusGroup) {
        allRecords = statusGroup.records.map((record: any) => ({
          ...record,
          statusCategory: status,
          slot_key: record.slot_name?.toLowerCase() || '',
          payment_type_key: record.payment_type_name?.toLowerCase() || '',
        }));
      }
    } else {
      // All statuses
      statusData.forEach(statusGroup => {
        statusGroup.records.forEach((record: any) => {
          allRecords.push({
            ...record,
            statusCategory: statusGroup.status,
            slot_key: record.slot_name?.toLowerCase() || '',
            payment_type_key: record.payment_type_name?.toLowerCase() || '',
          });
        });
      });
    }

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'contract_status', label: 'Status', type: 'status', width: '12%' },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = statusData.map(s => ({
      key: s.status,
      label: s.status,
    }));

    this.panelConfig.set({
      title: status ? `Arrangements: ${status}` : 'Arrangements by Contract Status',
      subtitle: `${allRecords.length} arrangements${status ? ` with ${status} status` : ' grouped by contract status'}`,
      data: allRecords,
      columns,
      filters,
      accentColor: status === 'Overdue' ? 'rose' : status === 'Active' ? 'emerald' : 'indigo',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for high remaining payments
  openHighRemainingPanel(): void {
    const records = this.highRemainingPayments();
    if (!records || records.length === 0) return;

    const allRecords = records.map((record: any) => ({
      ...record,
      slot_key: record.slot_name?.toLowerCase() || '',
      payment_type_key: record.payment_type_name?.toLowerCase() || '',
    }));

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'remaining_payments', label: 'Remaining', type: 'text', width: '10%' },
      { key: 'recurring_amount', label: 'Per Payment', type: 'currency', width: '12%' },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'gapRange', label: 'Gap', type: 'text', width: '10%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    this.panelConfig.set({
      title: 'High Remaining Payments (5+)',
      subtitle: `${allRecords.length} arrangements with significant remaining payment cycles`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'amber',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for arrangements by slot
  openSlotPanel(slot?: string): void {
    const slotData = this.recordsBySlot();
    if (!slotData || slotData.length === 0) return;

    let allRecords: any[] = [];

    if (slot) {
      const slotGroup = slotData.find(s => s.slot === slot);
      if (slotGroup) {
        allRecords = slotGroup.records.map((record: any) => ({
          ...record,
          slotCategory: slot,
          slot_key: record.slot_name?.toLowerCase() || slot.toLowerCase(),
          payment_type_key: record.payment_type_name?.toLowerCase() || '',
        }));
      }
    } else {
      slotData.forEach(slotGroup => {
        slotGroup.records.forEach((record: any) => {
          allRecords.push({
            ...record,
            slotCategory: slotGroup.slot,
            slot_key: record.slot_name?.toLowerCase() || slotGroup.slot.toLowerCase(),
            payment_type_key: record.payment_type_name?.toLowerCase() || '',
          });
        });
      });
    }

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'slot_name', label: 'Slot', type: 'badge', width: '10%' },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = slotData.map(s => ({
      key: s.slot.toLowerCase(),
      label: s.slot,
    }));

    this.panelConfig.set({
      title: slot ? `Arrangements: ${slot} Slot` : 'Arrangements by Time Slot',
      subtitle: `${allRecords.length} arrangements${slot ? ` scheduled for ${slot}` : ' grouped by execution time slot'}`,
      data: allRecords,
      columns,
      filters,
      accentColor: slot === 'Morning' ? 'amber' : slot === 'Afternoon' ? 'indigo' : slot === 'Evening' ? 'indigo' : 'indigo',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for top administrator's arrangements
  openTopAdminPanel(): void {
    const topAdmin = this.gapInsights()?.topAdmin;
    if (!topAdmin?.name) return;

    const pendingData = this.pendingByGap();
    if (!pendingData) return;

    const allRecords = pendingData.by_gap.flatMap(gap =>
      (gap.records || []).filter((record: any) => record.created_by === topAdmin.name).map((record: any) => ({
        ...record,
        gapRange: gap.gap_range,
        slot_key: record.slot_name?.toLowerCase() || '',
        payment_type_key: record.payment_type_name?.toLowerCase() || '',
      }))
    );

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'slot_name', label: 'Slot', type: 'text', width: '10%' },
      { key: 'gapRange', label: 'Gap', type: 'text', width: '10%' },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    this.panelConfig.set({
      title: `Arrangements by ${topAdmin.name}`,
      subtitle: `${allRecords.length} arrangements created by the top administrator`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'amber',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for peak month arrangements
  openPeakMonthPanel(): void {
    const peakMonth = this.gapInsights()?.peakMonth;
    if (!peakMonth?.label) return;

    const monthData = this.recordsByMonth();
    const peakMonthData = monthData.find(m => m.periodLabel === peakMonth.label);
    if (!peakMonthData?.records) return;

    const allRecords = peakMonthData.records.map((record: any) => ({
      ...record,
      slot_key: record.slot_name?.toLowerCase() || '',
      payment_type_key: record.payment_type_name?.toLowerCase() || '',
    }));

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'slot_name', label: 'Slot', type: 'text', width: '10%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    this.panelConfig.set({
      title: `Peak Month: ${peakMonth.label}`,
      subtitle: `${allRecords.length} arrangements scheduled in the busiest month`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'emerald',
      enableMonthYearFilter: false,
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for payment type arrangements
  openPaymentTypePanel(paymentType?: string): void {
    const paymentData = this.recordsByPaymentType();
    if (!paymentData || paymentData.length === 0) return;

    let allRecords: any[] = [];
    const targetType = paymentType || this.gapInsights()?.paymentType?.name;

    if (targetType) {
      const typeData = paymentData.find(p => p.paymentType === targetType);
      if (typeData?.records) {
        allRecords = typeData.records.map((record: any) => ({
          ...record,
          paymentTypeCategory: targetType,
          slot_key: record.slot_name?.toLowerCase() || '',
          payment_type_key: record.payment_type_name?.toLowerCase() || '',
        }));
      }
    } else {
      paymentData.forEach(typeGroup => {
        typeGroup.records.forEach((record: any) => {
          allRecords.push({
            ...record,
            paymentTypeCategory: typeGroup.paymentType,
            slot_key: record.slot_name?.toLowerCase() || '',
            payment_type_key: record.payment_type_name?.toLowerCase() || '',
          });
        });
      });
    }

    const columns: TransactionLogColumn[] = [
      { key: 'contract_code', label: 'Contract', type: 'text', width: '15%', searchable: true },
      { key: 'patient_name', label: 'Patient', type: 'text', width: '18%', searchable: true },
      { key: 'payment_type_name', label: 'Type', type: 'badge', width: '10%' },
      { key: 'arrangement_date', label: 'Date', type: 'date', width: '12%' },
      { key: 'scheduled_amount', label: 'Amount', type: 'currency', width: '12%' },
      { key: 'slot_name', label: 'Slot', type: 'text', width: '10%' },
      { key: 'created_by', label: 'Created By', type: 'text', width: '13%', searchable: true },
    ];

    const filters: TransactionLogFilter[] = [
      { key: 'recurring', label: 'Recurring' },
      { key: 'partial', label: 'Partial' },
    ];

    this.panelConfig.set({
      title: targetType ? `${targetType} Payments` : 'Arrangements by Payment Type',
      subtitle: `${allRecords.length} arrangements${targetType ? ` with ${targetType} payment type` : ''}`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'indigo',
      enableMonthYearFilter: true,
      dateFieldKey: 'arrangement_date',
    });

    this.isPanelOpen.set(true);
  }

  // Open panel for duplicates by admin
  openDuplicatesByAdminPanel(): void {
    const data = this.duplicatesByAdmin();
    if (!data?.records || data.records.length === 0) return;

    const allRecords = data.records.map((record: any) => ({
      admin_name: record.admin_name,
      admin_id: record.admin_id,
      duplicate_entries: record.duplicate_entries || 0,
      deletion_entries: record.deletion_entries || 0,
      contracts_with_duplicates: record.contracts_with_duplicates || 0,
      contracts_affected: record.contracts_affected || 0,
      total_null_entries: record.total_null_entries || 0,
    }));

    const columns: TransactionLogColumn[] = [
      { key: 'admin_name', label: 'Admin Name', type: 'text', width: '25%', searchable: true },
      { key: 'duplicate_entries', label: 'Duplicates', type: 'text', width: '15%' },
      { key: 'deletion_entries', label: 'Deletions', type: 'text', width: '15%' },
      { key: 'contracts_with_duplicates', label: 'Contracts w/ Duplicates', type: 'text', width: '18%' },
      { key: 'contracts_affected', label: 'Contracts Affected', type: 'text', width: '15%' },
      { key: 'total_null_entries', label: 'Null Entries', type: 'text', width: '12%' },
    ];

    const filters: TransactionLogFilter[] = [];

    this.panelConfig.set({
      title: 'Duplicates by Admin',
      subtitle: `${allRecords.length} administrators with duplicate or deleted arrangement entries`,
      data: allRecords,
      columns,
      filters,
      accentColor: 'rose',
    });

    this.isPanelOpen.set(true);
  }

  // Get table data for duplicates by admin (limited to 5 records for overview)
  getDuplicatesTableData(): any[] {
    const data = this.duplicatesByAdmin();
    if (!data?.records || data.records.length === 0) return [];

    return data.records.slice(0, 5).map((record: any) => ({
      admin_name: record.admin_name,
      admin_id: record.admin_id,
      duplicate_entries: record.duplicate_entries || 0,
      deletion_entries: record.deletion_entries || 0,
      contracts_with_duplicates: record.contracts_with_duplicates || 0,
      contracts_affected: record.contracts_affected || 0,
    }));
  }

  // Calculate percentage for circular progress
  getGapPercentage(gapTotal: number): number {
    const total = this.pendingByGap()?.summary?.total_pending ?? 0;
    if (total === 0) return 0;
    return Math.round((gapTotal / total) * 100);
  }

  // Get stroke-dashoffset for SVG circle (circumference = 2 * PI * radius)
  // For a circle with radius 18, circumference ≈ 113.1
  getCircleOffset(gapTotal: number): number {
    const percentage = this.getGapPercentage(gapTotal);
    const circumference = 2 * Math.PI * 18; // radius = 18
    return circumference - (percentage / 100) * circumference;
  }

  // Get color class for progress ring based on index
  getProgressColor(index: number, isLast: boolean): string {
    if (isLast) return 'stroke-red-500';
    switch (index) {
      case 0: return 'stroke-emerald-500';
      case 1: return 'stroke-blue-500';
      case 2: return 'stroke-violet-500';
      default: return 'stroke-amber-500';
    }
  }

  // Computed analytics data for charts
  recordsByMonth = this.analyticsService.recordsByMonth;
  recordsByYear = this.analyticsService.recordsByYear;
  arrangementsByCreatedAt = this.analyticsService.arrangementsByCreatedAt;
  recordsByStatus = this.analyticsService.recordsByStatus;
  recordsByPaymentType = this.analyticsService.recordsByPaymentType;
  recordsBySlot = this.analyticsService.recordsBySlot;
  highRemainingPayments = this.analyticsService.highRemainingPayments;

  // Recurring Analysis
  highestRecurringAmounts = this.analyticsService.highestRecurringAmounts;
  lowestRecurringAmounts = this.analyticsService.lowestRecurringAmounts;
  highestPartialAmounts = this.analyticsService.highestPartialAmounts;
  lowestPartialAmounts = this.analyticsService.lowestPartialAmounts;

  // Get status color classes
  getStatusColor(status: string): { bg: string; text: string; border: string; fill: string } {
    const colors: Record<string, { bg: string; text: string; border: string; fill: string }> = {
      'Overdue': {
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-700 dark:text-red-300',
        border: 'border-red-200 dark:border-red-700/50',
        fill: 'bg-red-500'
      },
      'Active': {
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        text: 'text-emerald-700 dark:text-emerald-300',
        border: 'border-emerald-200 dark:border-emerald-700/50',
        fill: 'bg-emerald-500'
      },
      'Completed': {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-200 dark:border-blue-700/50',
        fill: 'bg-blue-500'
      },
      'Pending': {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        text: 'text-amber-700 dark:text-amber-300',
        border: 'border-amber-200 dark:border-amber-700/50',
        fill: 'bg-amber-500'
      },
      'Unknown': {
        bg: 'bg-slate-50 dark:bg-slate-900/20',
        text: 'text-slate-700 dark:text-slate-300',
        border: 'border-slate-200 dark:border-slate-700/50',
        fill: 'bg-slate-500'
      }
    };
    return colors[status] || {
      bg: 'bg-violet-50 dark:bg-violet-900/20',
      text: 'text-violet-700 dark:text-violet-300',
      border: 'border-violet-200 dark:border-violet-700/50',
      fill: 'bg-violet-500'
    };
  }

  // Get status icon name
  getStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      'Overdue': 'alertCircle',
      'Active': 'checkCircle',
      'Completed': 'checkCircle2',
      'Pending': 'clock',
      'Unknown': 'helpCircle'
    };
    return icons[status] || 'circleAlert';
  }

  // Chart date unit toggle: 'month' or 'year'
  chartDateUnit = signal<'month' | 'year'>('month');

  toggleChartDateUnit(): void {
    this.chartDateUnit.set(this.chartDateUnit() === 'month' ? 'year' : 'month');
  }

  onChartDateUnitChange(unit: string): void {
    this.chartDateUnit.set(unit as 'month' | 'year');
  }

  distributionToggleConfig = computed<toggleHeader>(() => ({
    showToggleButton: true,
    showlabels: true,
    size: 'sm',
    activeTextColor: '#AD46FF',
    inactiveTextColor: this.themeService.theme() === 'dark' ? '#A1A1AA' : '#64748b',
    toggleOptions: [
      { label: 'Month', value: 'month', tooltip: 'View by Month', isTable: false },
      { label: 'Year', value: 'year', tooltip: 'View by Year', isTable: true }
    ]
  }));

  paymentToggleConfig = computed<toggleHeader>(() => ({
    showToggleButton: true,
    showlabels: true,
    showHeading: false,
    size: 'sm',
    activeTextColor: '#AD46FF',
    inactiveTextColor: this.themeService.theme() === 'dark' ? '#A1A1AA' : '#64748b',
    toggleOptions: [
      { label: 'Recurring', value: 'recurring', tooltip: 'View Recurring Payments', isTable: false },
      { label: 'Partial', value: 'partial', tooltip: 'View Partial Payments', isTable: true }
    ]
  }));

  // Computed: Arrangements by Created At grouped by Year
  arrangementsByCreatedAtByYear = computed<RecordsByPeriod[]>(() => {
    const records = this.analyticsService.getAllRecords().filter(r => r['days_until'] <= 90);
    if (records.length === 0) return [];

    const byYear = new Map<string, { records: any[], totalAmount: number }>();

    records.forEach(record => {
      const createdAt = record['created_at'];
      if (!createdAt) return;

      const date = new Date(createdAt);
      const yearKey = String(date.getFullYear());

      if (!byYear.has(yearKey)) {
        byYear.set(yearKey, { records: [], totalAmount: 0 });
      }
      const entry = byYear.get(yearKey)!;
      entry.records.push(record);
      const paymentType = record['payment_type_name'];
      const amount = paymentType === 'Recurring'
        ? parseFloat(record['recurring_amount'] || '0')
        : parseFloat(record['partial_amount'] || '0');
      entry.totalAmount += amount;
    });

    return Array.from(byYear.entries())
      .map(([period, data]) => {
        const recurringCount = data.records.filter((r: any) => r['payment_type_name'] === 'Recurring').length;
        const avgDaysUntil = data.records.reduce((sum: number, r: any) => sum + (r['days_until'] || 0), 0) / data.records.length;
        return {
          period,
          periodLabel: period,
          count: data.records.length,
          totalAmount: data.totalAmount,
          recurringCount,
          partialCount: data.records.length - recurringCount,
          avgDaysUntil,
          records: data.records
        };
      })
      .sort((a, b) => a.period.localeCompare(b.period));
  });

  // Chart series for Distribution by Arrangement Date (supports month/year)
  arrangementDateChartSeries = computed<ILineAreaSeries[]>(() => {
    const unit = this.chartDateUnit();
    const data = unit === 'month'
      ? this.analyticsService.recordsByMonth()
      : this.analyticsService.recordsByYear();

    if (data.length === 0) return [];

    // Filter out future dates
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const filteredData = data.filter((record: RecordsByPeriod) => {
      if (unit === 'month') {
        const [yearStr, monthStr] = record.period.split('-');
        if (!yearStr || !monthStr) return true;
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10);
        if (year > currentYear) return false;
        if (year === currentYear && month > currentMonth) return false;
        return true;
      } else {
        const year = parseInt(record.period, 10);
        if (isNaN(year)) return true;
        return year <= currentYear;
      }
    });

    const dataPoints: ILineAreaDataPoint[] = filteredData.map((record: RecordsByPeriod) => ({
      date: record.period,
      value: record.count,
      label: record.periodLabel,
      metadata: {
        totalAmount: record.totalAmount,
        recurringCount: record.recurringCount,
        partialCount: record.partialCount,
        avgDaysUntil: record.avgDaysUntil
      }
    }));

    return [{
      key: 'arrangement',
      name: 'By Arrangement',
      data: dataPoints,
      lineColor: '#3b82f6',
      areaColor: '#3b82f6',
      areaOpacity: 0.1,
      strokeWidth: 2,
      showPoints: false,
    }];
  });

  // Chart series for Distribution by Creation Date (supports month/year)
  creationDateChartSeries = computed<ILineAreaSeries[]>(() => {
    const unit = this.chartDateUnit();
    const data = unit === 'month'
      ? this.analyticsService.arrangementsByCreatedAt()
      : this.arrangementsByCreatedAtByYear();

    if (data.length === 0) return [];

    const dataPoints: ILineAreaDataPoint[] = data.map((record: RecordsByPeriod) => ({
      date: record.period,
      value: record.count,
      label: record.periodLabel,
      metadata: {
        totalAmount: record.totalAmount,
        recurringCount: record.recurringCount,
        partialCount: record.partialCount,
        avgDaysUntil: record.avgDaysUntil
      }
    }));

    return [{
      key: 'creation',
      name: 'By Creation',
      data: dataPoints,
      lineColor: '#a855f7',
      areaColor: '#a855f7',
      areaOpacity: 0.05,
      strokeWidth: 2,
      showPoints: false,
      lineStyle: 'dashed',
    }];
  });



  // Custom tooltip formatter for the charts
  chartTooltipFormatter = (value: number, series: ILineAreaSeries, point: ILineAreaDataPoint): string => {
    const meta = point.metadata;
    if (!meta) return value.toString();

    const totalAmount = meta['totalAmount'] as number | undefined;
    const recurringCount = meta['recurringCount'] as number | undefined;
    const partialCount = meta['partialCount'] as number | undefined;
    const avgDaysUntil = meta['avgDaysUntil'] as number | undefined;

    return `
      <div style="font-size: 11px; line-height: 1.4;">
        <div style="display: flex; justify-content: space-between; gap: 12px;">
          <span style="opacity: 0.7;">Amount:</span>
          <span style="font-weight: 500;">$${totalAmount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</span>
        </div>
        <div style="display: flex; justify-content: space-between; gap: 12px;">
          <span style="opacity: 0.7;">Recurring:</span>
          <span style="font-weight: 500;">${recurringCount || 0}</span>
        </div>
        <div style="display: flex; justify-content: space-between; gap: 12px;">
          <span style="opacity: 0.7;">Partial:</span>
          <span style="font-weight: 500;">${partialCount || 0}</span>
        </div>
        <div style="display: flex; justify-content: space-between; gap: 12px;">
          <span style="opacity: 0.7;">Avg Days:</span>
          <span style="font-weight: 500;">${Math.round(avgDaysUntil || 0)}</span>
        </div>
      </div>
    `;
  };

  private computeGapInsights(pendingByGap: PendingByGapAPI | null, records: any[]): void {
    if (!pendingByGap || !records || records.length === 0) {
      this.gapInsights.set({
        topAdmin: null,
        peakMonth: null,
        paymentType: null,
        avgDays: null,
        recurring: null,
        partial: null,
        totalPending: 0,
      });
      return;
    }

    // 1. Top Administrator - find admin with most arrangements
    const adminMap = new Map<string, { count: number; totalAmount: number }>();
    records.forEach((record: any) => {
      const adminName = record['created_by'] || 'Unknown';
      const amount = parseFloat(record['scheduled_amount']) || 0;
      if (adminMap.has(adminName)) {
        const existing = adminMap.get(adminName)!;
        adminMap.set(adminName, {
          count: existing.count + 1,
          totalAmount: existing.totalAmount + amount,
        });
      } else {
        adminMap.set(adminName, { count: 1, totalAmount: amount });
      }
    });

    let topAdmin: { name: string; count: number; totalAmount: number } | null = null;
    adminMap.forEach((value, key) => {
      if (!topAdmin || value.count > topAdmin.count) {
        topAdmin = { name: key, count: value.count, totalAmount: value.totalAmount };
      }
    });

    // 2. Peak Month - find month with most arrangements
    const monthMap = new Map<string, { count: number; totalAmount: number }>();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    records.forEach((record: any) => {
      const dateStr = record['arrangement_date'] || record['scheduled_date'];
      if (dateStr) {
        const date = new Date(dateStr);
        const monthYear = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        const amount = parseFloat(record['scheduled_amount']) || 0;

        if (monthMap.has(monthYear)) {
          const existing = monthMap.get(monthYear)!;
          monthMap.set(monthYear, {
            count: existing.count + 1,
            totalAmount: existing.totalAmount + amount,
          });
        } else {
          monthMap.set(monthYear, { count: 1, totalAmount: amount });
        }
      }
    });

    let peakMonth: { label: string; count: number; totalAmount: number } | null = null;
    monthMap.forEach((value, key) => {
      if (!peakMonth || value.count > peakMonth.count) {
        peakMonth = { label: key, count: value.count, totalAmount: value.totalAmount };
      }
    });

    // 3. Primary Payment Type - count recurring vs partial
    let recurringCount = 0;
    let partialCount = 0;
    let recurringAmount = 0;
    let partialAmount = 0;

    records.forEach((record: any) => {
      const paymentType = record['payment_type_name']?.toLowerCase() || '';
      const amount = parseFloat(record['scheduled_amount']) || 0;
      if (paymentType.includes('recurring')) {
        recurringCount++;
        recurringAmount += amount;
      } else if (paymentType.includes('partial')) {
        partialCount++;
        partialAmount += amount;
      }
    });

    const totalRecords = records.length;
    let paymentType: { name: string; count: number; percentage: number; totalAmount: number } | null = null;

    if (recurringCount >= partialCount) {
      paymentType = {
        name: 'Recurring',
        count: recurringCount,
        percentage: totalRecords > 0 ? Math.round((recurringCount / totalRecords) * 100 * 10) / 10 : 0,
        totalAmount: recurringAmount,
      };
    } else {
      paymentType = {
        name: 'Partial',
        count: partialCount,
        percentage: totalRecords > 0 ? Math.round((partialCount / totalRecords) * 100 * 10) / 10 : 0,
        totalAmount: partialAmount,
      };
    }

    // 4. Average Days Until Execution
    const byGap = pendingByGap.by_gap || [];
    let totalDays = 0;
    let countDays = 0;
    let earliestDate: string | null = null;
    let latestDate: string | null = null;

    byGap.forEach((gap) => {
      if (gap.avg_days_until) {
        totalDays += gap.avg_days_until * gap.total_arrangements;
        countDays += gap.total_arrangements;
      }
      if (gap.earliest_date) {
        if (!earliestDate || gap.earliest_date < earliestDate) {
          earliestDate = gap.earliest_date;
        }
      }
      if (gap.latest_date) {
        if (!latestDate || gap.latest_date > latestDate) {
          latestDate = gap.latest_date;
        }
      }
    });

    const avgDaysValue = countDays > 0 ? Math.round(totalDays / countDays) : 0;

    // Calculate percentages for recurring and partial
    const recurringPercentage = totalRecords > 0 ? Math.round((recurringCount / totalRecords) * 100 * 10) / 10 : 0;
    const partialPercentage = totalRecords > 0 ? Math.round((partialCount / totalRecords) * 100 * 10) / 10 : 0;

    this.gapInsights.set({
      topAdmin,
      peakMonth,
      paymentType,
      avgDays: {
        value: avgDaysValue,
        earliest: earliestDate || 'N/A',
        latest: latestDate || 'N/A',
      },
      recurring: {
        count: recurringCount,
        totalAmount: recurringAmount,
        percentage: recurringPercentage,
      },
      partial: {
        count: partialCount,
        totalAmount: partialAmount,
        percentage: partialPercentage,
      },
      totalPending: totalRecords,
    });
  }

  // ─── Pin handlers ──────────────────────────────────────────────────────
  private _toSectionId(
    sec: { id: ArrangementCronOverviewSectionId } | ArrangementCronOverviewSectionId,
  ): ArrangementCronOverviewSectionId {
    return typeof sec === 'object' && sec !== null ? sec.id : sec;
  }

  isAddedToCustomDashboard(
    sec: { id: ArrangementCronOverviewSectionId } | ArrangementCronOverviewSectionId,
  ): boolean {
    return this.pinnedSections().has(this._toSectionId(sec));
  }

  isAddingToCustomDashboard(
    sec: { id: ArrangementCronOverviewSectionId } | ArrangementCronOverviewSectionId,
  ): boolean {
    return this.pendingPinSections().has(this._toSectionId(sec));
  }

  canPinToCustomDashboard(
    sec: { id: ArrangementCronOverviewSectionId } | ArrangementCronOverviewSectionId,
  ): boolean {
    if (this.mixedMode()) return false;
    return !!this._toSectionId(sec);
  }

  addToCustomDashboard(
    sec: { id: ArrangementCronOverviewSectionId } | ArrangementCronOverviewSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      this.toaster?.info?.(
        'Pinning Arrangement Cron overview sections to your Custom Dashboard will be available once this page is registered in the dashboards catalog.',
      );
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.apiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Layout editor (public entry for parent shell) ─────────────────────
  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: OverviewLayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      defaultColSpan: this._normalizeColSpan(
        ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
      ),
    }));

    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: string): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    const defaults: OverviewLayoutEditorItem[] = this.SECTIONS.map((s, idx) => {
      const defaultSpan = this._normalizeColSpan(
        ARRANGMENT_CRON_DEFAULT_COL_SPAN[s.id] ?? 12,
      );
      return {
        id: s.id as ArrangementCronOverviewSectionId,
        title: s.title,
        visible: true,
        colSpan: defaultSpan,
        order: idx,
        defaultColSpan: defaultSpan,
      };
    });
    this.editorItems.set(defaults);
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    if (!draft.some((d) => d.visible)) {
      this.toaster?.warning?.(
        'At least one section must be visible. Toggle a section on before applying.',
      );
      return;
    }

    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.toaster?.success?.('Layout updated for Overview.');

    this.isLayoutEditorOpen.set(false);
  }

  decreaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(
          ARRANGMENT_CRON_DEFAULT_COL_SPAN[i.id] ?? 12,
        );
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: string): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  onEditorCardDrop(event: CdkDragDrop<OverviewLayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (!next) return;
    this.editorItems.set(next);
    this._syncLayoutFromEditor();
  }

  private _buildLayoutFromEditor(): Record<
    ArrangementCronOverviewSectionId,
    OverviewSectionLayoutItem
  > {
    const map = buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS,
      ARRANGMENT_CRON_DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
    return map as Record<ArrangementCronOverviewSectionId, OverviewSectionLayoutItem>;
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<ArrangementCronOverviewSectionId, OverviewSectionLayoutItem> =
      {} as Record<ArrangementCronOverviewSectionId, OverviewSectionLayoutItem>;

    ArrangementCronOverviewSectionDef.forEach((s, idx) => {
      const sid = s.id as ArrangementCronOverviewSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? ARRANGMENT_CRON_DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    if (!Object.values(layout).some((l) => l.visible)) {
      ArrangementCronOverviewSectionDef.forEach((s, idx) => {
        const sid = s.id as ArrangementCronOverviewSectionId;
        layout[sid] = {
          id: sid,
          visible: true,
          colSpan: this._normalizeColSpan(ARRANGMENT_CRON_DEFAULT_COL_SPAN[sid] ?? 12),
          order: idx,
        };
      });
    }

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, OverviewSectionLayoutItem> | null {
    try {
      const raw =
        localStorage.getItem(this.LAYOUT_LS_KEY) ??
        localStorage.getItem('prism_arrangment_cron_ac_overview_layout_v2') ??
        localStorage.getItem('prism_arrangment_cron_ac_overview_layout_v1');
      if (!raw) return null;
      return this._migrateLegacyOverviewLayout(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  /** Maps retired combined section keys to their split layout sections. */
  private _migrateLegacyOverviewLayout(
    stored: Record<string, OverviewSectionLayoutItem>,
  ): Record<string, OverviewSectionLayoutItem> {
    let migrated = { ...stored };

    migrated = this._migrateLegacySection(migrated, 'ov_live_execution', [
      { id: ArrangementCronOverviewSectionId.SCHEDULED_TODAY, colSpan: 6, orderOffset: 0 },
      { id: ArrangementCronOverviewSectionId.RECENT_ATTEMPTS, colSpan: 6, orderOffset: 1 },
      { id: ArrangementCronOverviewSectionId.KPI_SUMMARY, colSpan: 12, orderOffset: 2 },
    ]);

    migrated = this._migrateLegacySection(migrated, 'ov_distribution_analysis', [
      { id: ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION, colSpan: 6, orderOffset: 0 },
      { id: ArrangementCronOverviewSectionId.CREATION_LIFECYCLE, colSpan: 6, orderOffset: 1 },
    ]);

    const legacyKeyMap: Record<string, ArrangementCronOverviewSectionId> = {
      ov_system_intel: ArrangementCronOverviewSectionId.SYSTEM_INTEL,
      ov_scheduled_today: ArrangementCronOverviewSectionId.SCHEDULED_TODAY,
      ov_recent_attempts: ArrangementCronOverviewSectionId.RECENT_ATTEMPTS,
      ov_kpi_metrics: ArrangementCronOverviewSectionId.KPI_SUMMARY,
      ov_pending_summary: ArrangementCronOverviewSectionId.TIME_GAP_DISTRIBUTION,
      ov_quick_actions: ArrangementCronOverviewSectionId.QUICK_ACTION,
      ov_receivable_aging: ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW,
      ov_time_slots: ArrangementCronOverviewSectionId.AR_AGING_OVERVIEW,
      ov_arrangement_distribution: ArrangementCronOverviewSectionId.MONTHLY_DISTRIBUTION,
      ov_creation_date_lifecycle: ArrangementCronOverviewSectionId.CREATION_LIFECYCLE,
      ov_contract_status: ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART,
      ov_high_remaining: ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART,
      ov_payment_amount: ArrangementCronOverviewSectionId.CONTRACT_STATUS_CHART,
      ov_duplicates: ArrangementCronOverviewSectionId.METRIC_ANOMALIES,
    };

    for (const [legacyKey, nextKey] of Object.entries(legacyKeyMap)) {
      if (migrated[legacyKey] && !migrated[nextKey]) {
        migrated[nextKey] = { ...migrated[legacyKey], id: nextKey };
      }
      delete migrated[legacyKey];
    }

    return migrated;
  }

  private _migrateLegacySection(
    stored: Record<string, OverviewSectionLayoutItem>,
    legacyKey: string,
    splitSections: Array<{
      id: ArrangementCronOverviewSectionId;
      colSpan: number;
      orderOffset: number;
    }>,
  ): Record<string, OverviewSectionLayoutItem> {
    const legacy = stored[legacyKey];
    if (!legacy) return stored;

    const migrated = { ...stored };
    delete migrated[legacyKey];

    for (const { id, colSpan, orderOffset } of splitSections) {
      if (!migrated[id]) {
        migrated[id] = {
          id,
          visible: legacy.visible,
          colSpan,
          order: legacy.order + orderOffset,
        };
      }
    }

    return migrated;
  }

  private _saveLayoutToStorage(
    layout: Record<ArrangementCronOverviewSectionId, OverviewSectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    return normalizeLayoutEditorColSpan(span);
  }

  private _persistLayoutToBackend(
    draft: ReadonlyArray<OverviewLayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    if (cachedPageId == null) return;

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe(() => {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: cachedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }
}
